import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './UserAndPartner.css';

export default function UserAndPartner() {
  const navigate = useNavigate();

  // =====================================================
  // DEVICE / BROWSER NOTIFICATION
  // =====================================================

  useEffect(() => {
    const showWelcomeNotification = async () => {
      // Browser notification support check
      if (!('Notification' in window)) {
        return;
      }

      try {
        // -------------------------------------------------
        // REQUEST PERMISSION
        // -------------------------------------------------

        if (Notification.permission === 'default') {
          const permission = await Notification.requestPermission();

          if (permission !== 'granted') {
            return;
          }
        }

        // -------------------------------------------------
        // SHOW NOTIFICATION
        // -------------------------------------------------

        if (Notification.permission === 'granted') {
          const notification = new Notification(
            'Welcome BroBizee',
            {
              body: 'Welcome to BroBizee! Everything You Need, Ends With BroBizee.',
              icon: '/images/logo.png',
              badge: '/images/logo.png',
              tag: 'brobizee-welcome',
              renotify: false,
              requireInteraction: false,
            }
          );

          // -------------------------------------------------
          // CLICK NOTIFICATION
          // -------------------------------------------------

          notification.onclick = () => {
            window.focus();
            notification.close();
          };
        }
      } catch (error) {
        console.error(
          'BroBizee notification error:',
          error
        );
      }
    };

    // Small delay so page loads first
    const timer = setTimeout(() => {
      showWelcomeNotification();
    }, 800);

    return () => clearTimeout(timer);
  }, []);

  // =====================================================
  // GET USER ID + DEVICE UUID
  // =====================================================

  const getUserPath = () => {
    const userId = localStorage.getItem('user_id');
    const deviceUUID = localStorage.getItem('device_uuid');

    if (!userId || !deviceUUID) {
      alert(
        'User ID or Device ID not found. Please restart registration.'
      );
      return null;
    }

    return {
      userId,
      deviceUUID
    };
  };

  // =====================================================
  // PARTNER
  // =====================================================

  const handlePartnerSelect = () => {
    const data = getUserPath();

    if (!data) return;

    navigate(
      `/${data.userId}/${data.deviceUUID}/partner_login`
    );
  };

  // =====================================================
  // USER
  // =====================================================

  const handleUserSelect = () => {
    const data = getUserPath();

    if (!data) return;

    navigate(
      `/${data.userId}/${data.deviceUUID}/userprofile`
    );
  };

  // =====================================================
  // JOIN INVENTORY
  // =====================================================

  const handleJoinInventory = () => {
    const data = getUserPath();

    if (!data) return;

    navigate(
      `/${data.userId}/${data.deviceUUID}/inventory`
    );
  };

  return (
    <div className="role-page-wrapper">

      <div className="role-app-card">

        {/* Ambient Background */}
        <div className="role-ambient role-ambient-one"></div>
        <div className="role-ambient role-ambient-two"></div>

        {/* =========================
            HEADER
        ========================= */}

        <header className="role-header">

          <button
            type="button"
            className="role-back-btn"
            aria-label="Go Back"
            onClick={() => navigate(-1)}
          >

            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M15 18l-6-6 6-6" />
            </svg>

          </button>

          <div className="role-progress">

            <span className="role-progress-dot"></span>

            <span className="role-progress-dot"></span>

            <span className="role-progress-dot"></span>

            <span className="role-progress-dot active"></span>

          </div>

        </header>

        {/* =========================
            MAIN CONTENT
        ========================= */}

        <main className="role-content">

          <span className="role-eyebrow">
            GET STARTED
          </span>

          <h1 className="role-title">
            How will you
            <span> use BroBizee?</span>
          </h1>

          <p className="role-subtitle">
            Choose the experience that best fits
            your needs.
          </p>

          {/* =========================
              ROLE CARDS
          ========================= */}

          <div className="role-buttons-grid">

            {/* =========================
                PARTNER
            ========================= */}

            <button
              type="button"
              className="role-card partner-card"
              onClick={handlePartnerSelect}
            >

              <div className="role-card-top">

                <div className="role-icon partner-icon">

                  <svg
                    width="25"
                    height="25"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >

                    <path d="M20 7h-4V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2H4a2 2 0 00-2 2v9a2 2 0 002 2h16a2 2 0 002-2V9a2 2 0 00-2-2z" />

                    <path d="M8 7V5h8v2" />

                    <path d="M9 13h6" />

                    <path d="M12 10v6" />

                  </svg>

                </div>

                <span className="role-arrow">

                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M5 12h14" />
                    <path d="M13 6l6 6-6 6" />
                  </svg>

                </span>

              </div>

              <div className="role-card-text">

                <span className="role-card-small">
                  I WANT TO PROVIDE
                </span>

                <strong>
                  BROBIZEE PARTNER
                </strong>

                <p>
                  Offer your skills and services
                  to customers.
                </p>

              </div>

            </button>

            {/* =========================
                USER
            ========================= */}

            <button
              type="button"
              className="role-card user-card"
              onClick={handleUserSelect}
            >

              <div className="role-card-top">

                <div className="role-icon user-icon">

                  <svg
                    width="25"
                    height="25"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >

                    <circle
                      cx="12"
                      cy="8"
                      r="3.5"
                    />

                    <path
                      d="M5 20c.8-4 3.1-6 7-6s6.2 2 7 6"
                    />

                  </svg>

                </div>

                <span className="role-arrow">

                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M5 12h14" />
                    <path d="M13 6l6 6-6 6" />
                  </svg>

                </span>

              </div>

              <div className="role-card-text">

                <span className="role-card-small">
                  I WANT TO FIND
                </span>

                <strong>
                  BROBIZEE USER
                </strong>

                <p>
                  Discover trusted professionals
                  for your needs.
                </p>

              </div>

            </button>

          </div>

        </main>

        {/* =========================
            FOOTER
        ========================= */}

        <footer className="role-footer">

          <img
            src="/images/logo.png"
            alt="BroBizee Logo"
            className="brand-logo-img"
          />

          <span>
            Everything You Need, Ends With BroBizee.
          </span>

        </footer>

      </div>

    </div>
  );
}